<script lang="ts" setup>
import { useSiteProContentStore } from '~/store/shipzeeProContentStore'
import getRequestAdvancedPhotosPrice from '~/utils/shipzeePro/getRequestAdvancedPhotosPrice'
import getRequestAdvancedPhotosDate from '~/utils/shipzeePro/getRequestAdvancedPhotosDate'
import getInspectPackagePrice from '~/utils/shipzeePro/getInspectPackagePrice'
import getInspectPackageDate from '~/utils/shipzeePro/getInspectPackageDate'
import { getFullYear } from '~/utils/date/parseDate'
import toPrice from '~/utils/toPrice'
import ReadyShipmentStatus from '~/components/SitePro/Tables/ReadyShipmentStatus.vue'
import ModalShipmentItemGallery from '~/components/SitePro/Modals/ModalShipmentItemGallery.vue'
import AdditionalServicesDropdown from '~/components/SitePro/AdditionalServicesDropdown.vue'
import PackageAdditionalServiceEnum from '~/enums/PackageAdditionalServiceEnum'
import { Routes } from '~/enums/Routes'
import getLocalizedRoute from '~/utils/getLocalizedRoute'
import copyIcon from '~/assets/icons/copy-icon.svg'
import type { PackageAdditionalServiceInterface, PackageInterface } from '~/types/shipzeePro/packagesTypes'
import Tooltip from '~/components/Tooltip.vue'
import ModalInspectPackage from '~/components/SitePro/Modals/ModalInspectPackage.vue'
import ModalExtraPhotos from '~/components/SitePro/Modals/ModalExtraPhotos.vue'
import ModalAbandonPackage from '~/components/SitePro/Modals/ModalAbandonPackage.vue'
import ModalServicesResults from '~/components/SitePro/Modals/ModalServicesResults.vue'
import ReadyShipmentsTableRow from '~/components/SitePro/ReadyShipmentsTableRow.vue'

const { t } = useLang()
const contentStore = useSiteProContentStore()
const perPage = 20

const route = useRoute()

const successMessage = ref('')
const errorMessage = ref('')

watch(
  () => route.query,
  (newQuery) => {
    successMessage.value = ''

    const frId = newQuery.id ?? null

    if (frId) {
      if (!isNaN(+frId)) {
        successMessage.value = t('site_pro.delivery_created', {
          id: String(frId),
        })
      } else {
        errorMessage.value = t('site_pro.delivery_error')
      }
    }
  },
  {
    immediate: true,
  }
)

const currentPageItems = computed(() =>
  contentStore.readyItems.slice(
    contentStore.readyItemsCurrentPage * perPage - perPage,
    contentStore.readyItemsCurrentPage * perPage
  )
)

const selectedPackages = ref<PackageInterface[]>([])
const openedPackages = ref<PackageInterface[]>([])

const isModalVisible = ref(false)
const isModalInspectPackageVisible = ref(false)
const isModalServicesResultsVisible = ref(false)
const isModalExtraPhotosVisible = ref(false)
const isModalAbandonPackageVisible = ref(false)

const itemIdServiceModal = ref<number | null>(null)
const itemToShowGallery = ref<PackageInterface | null>(null)
const itemToShowServicesResults = ref<PackageInterface | null>(null)

const areAllPackagesSelected = computed(() => {
  let allSelected = true
  currentPageItems.value.forEach((item) => {
    if (!selectedPackages.value.includes(item)) {
      allSelected = false
    }
  })

  return allSelected
})

const areAllPackagesOpened = computed(() => {
  let allOpened = true
  currentPageItems.value.forEach((item) => {
    if (!openedPackages.value.includes(item)) {
      allOpened = false
    }
  })

  return allOpened
})

function toggleSelectAll() {
  const areAllSelected = areAllPackagesSelected.value
  selectedPackages.value = selectedPackages.value.filter((pkg) => !currentPageItems.value.includes(pkg))
  if (!areAllSelected) {
    selectedPackages.value = selectedPackages.value.concat(currentPageItems.value)
  }
}

function toggleOpenAll() {
  const areAllOpened = areAllPackagesOpened.value
  openedPackages.value = openedPackages.value.filter((pkg) => !currentPageItems.value.includes(pkg))
  if (!areAllOpened) {
    openedPackages.value = openedPackages.value.concat(currentPageItems.value)
  }
}

function toggleSelectRow(shipment: PackageInterface) {
  if (selectedPackages.value.includes(shipment)) {
    selectedPackages.value = selectedPackages.value.filter((shipmentPackage) => shipmentPackage.id !== shipment.id)
  } else {
    selectedPackages.value.push(shipment)
  }
}

function toggleOpenRow(shipment: PackageInterface) {
  if (openedPackages.value.includes(shipment)) {
    openedPackages.value = openedPackages.value.filter((shipmentPackage) => shipmentPackage.id !== shipment.id)
  } else {
    openedPackages.value.push(shipment)
  }
}

function handleVisibility(value: boolean) {
  isModalVisible.value = value
}

function showGalleryModal(shipment: PackageInterface) {
  itemToShowGallery.value = shipment
  isModalVisible.value = true
}

function handleOpenServiceModal(serviceId: PackageAdditionalServiceEnum, itemId: number) {
  itemIdServiceModal.value = itemId
  successMessage.value = ''
  errorMessage.value = ''

  switch (serviceId) {
    case PackageAdditionalServiceEnum.InspectPackage:
      isModalInspectPackageVisible.value = true
      break
    case PackageAdditionalServiceEnum.ExtraPhotos:
      isModalExtraPhotosVisible.value = true
      break
    case PackageAdditionalServiceEnum.AbandonPackage:
      isModalAbandonPackageVisible.value = true
      break
  }
}

function handleOpenServicesResultsModal(shipment: PackageInterface) {
  itemToShowServicesResults.value = shipment
  isModalServicesResultsVisible.value = true
}

function handleSuccessRequestSubmit(itemId: number, serviceId: number) {
  successMessage.value = t('site_pro.additional_services.success_submit_message')

  const item = contentStore.readyItems.find((item: PackageInterface) => item.id === itemId)

  if (item) {
    const service: PackageAdditionalServiceInterface = {
      service_id: serviceId,
      completed: 0,
      completed_at: null,
      service_price: null,
      arguments: {
        instructions: '',
      },
    }

    item.additional_services.push(service)

    contentStore.prependInReviewItem(item)
  }

  contentStore.setReadyItems(contentStore.readyItems.filter((item: PackageInterface) => item.id !== itemId))
}

function startDelivery() {
  if (selectedPackages.value.length > 0) {
    contentStore.resetDeliveryState()
    contentStore.setSelectedItems(selectedPackages.value)

    return navigateTo(getLocalizedRoute(Routes.Account.SiteProDelivery))
  }
}
</script>

<template>
  <div>
    <div
      v-if="contentStore.readyItems.length"
      class="pro-table xl:bg-white dark:bg-dmGray2 p-2 xl:p-5 rounded-b-[2rem] xl:rounded-r-[2rem]">
      <ShipmentTableAlerts :success-message="successMessage" :error-message="errorMessage" />
      <div class="table-desc text-sm pb-4 pt-5 xl:px-0 xl:pt-0">{{ t('site_pro.all_shipments_below_are_ready') }}</div>
      <div class="received-table">
        <div class="received-header">
          <div class="header-item-1 flex items-center">
            <div class="checkbox-decorator" @click="toggleSelectAll">
              <i v-if="areAllPackagesSelected" class="fa-solid fa-square-check fa-fw text-brand-primary text-xl" />
              <i v-else class="fa-regular fa-square fa-fw text-gray5 dark:text-dmText1 text-xl" />
            </div>
          </div>
          <div class="header-status" />
          <div class="header-item-2">{{ t('site_pro.table.source') }}</div>
          <div class="header-item-3">{{ t('site_pro.table.weight') }}</div>
          <div class="header-item-4">{{ t('site_pro.table.date') }}</div>
          <div class="header-item-5">{{ t('site_pro.table.shipment_id') }}</div>
          <div class="header-item-6">
            <div>
              <div
                class="flex items-center justify-around w-10 h-10 bg-white dark:bg-dmGray1 rounded-full cursor-pointer wl:bg-neutral7"
                @click="toggleOpenAll">
                <i
                  class="fa-solid"
                  :class="{
                    'fa-chevron-up text-brand-primary': areAllPackagesOpened,
                    'fa-chevron-down': !areAllPackagesOpened,
                  }" />
              </div>
            </div>
          </div>
        </div>
        <div class="ready-body">
          <div v-for="(shipment, index) in currentPageItems" :key="index" class="body-item">
            <ReadyShipmentsTableRow
              :shipment="shipment"
              :opened-packages="openedPackages"
              :selected-packages="selectedPackages"
              @handle-open-service-modal="handleOpenServiceModal"
              @handle-open-services-results-modal="handleOpenServicesResultsModal"
              @show-gallery-modal="showGalleryModal"
              @toggle-select-row="toggleSelectRow"
              @toggle-open-row="toggleOpenRow" />
          </div>
        </div>
      </div>

      <div class="ready-table-mobile">
        <div
          class="all-toggle bg-white dark:bg-dmGray1 py-3 px-6 mb-4 rounded-sm flex items-center gap-2"
          @click.prevent="toggleSelectAll">
          <div class="checkbox-decorator">
            <i v-if="areAllPackagesSelected" class="fa-solid fa-square-check fa-fw text-brand-primary text-xl" />
            <i v-else class="fa-regular fa-square fa-fw text-gray5 dark:text-dmText1 text-xl" />
          </div>
          <span v-if="!areAllPackagesSelected" class="font-bold">{{ t('site_pro.table.select_all') }}</span>
          <span v-else class="font-bold">{{ t('site_pro.table.deselect_all') }}</span>
        </div>
        <div
          v-for="(shipment, index) in currentPageItems"
          :key="index"
          class="mobile-body-item"
          :class="{ 'open-item': openedPackages.includes(shipment) || selectedPackages.includes(shipment) }">
          <div class="flex items-center gap-4">
            <div class="checkbox-decorator" @click="toggleSelectRow(shipment)">
              <i
                v-if="!selectedPackages.includes(shipment)"
                class="fa-regular fa-square fa-fw text-gray5 dark:text-dmText1 text-xl" />
              <i v-else class="fa-solid fa-square-check fa-fw text-brand-primary text-xl" />
            </div>
            <div class="flex flex-col max-w-[58%]">
              <div class="flex flex-shrink gap-1">
                <span>{{ shipment.source }}</span>
                <ReadyShipmentStatus :item="shipment" />
              </div>
              <div class="text-neutral3 dark:text-dmText3 text-sm pt-1 overflow-hidden overflow-ellipsis">
                {{ shipment.source_tracking }}
              </div>
            </div>
            <div class="ml-auto">
              <div
                class="toggle flex items-center justify-around w-10 h-10 bg-neutral7 dark:bg-dmGray3 rounded-full cursor-pointer"
                @click="toggleOpenRow(shipment)">
                <i
                  class="fa-solid"
                  :class="{
                    'fa-chevron-up text-brand-primary': openedPackages.includes(shipment),
                    'fa-chevron-down': !openedPackages.includes(shipment),
                  }" />
              </div>
            </div>
          </div>
          <div class="horizontal-divider" />
          <div class="mobile-copy-button">
            <img :src="copyIcon" alt="copy" />
            <span>{{ t('site_pro.table.copy_shipment_tracking_number') }}</span>
          </div>
          <div v-if="openedPackages.includes(shipment)">
            <div class="horizontal-divider" />
            <div class="flex flex-col gap-4">
              <div class="flex justify-between text-sm">
                <div>{{ t('site_pro.table.weight') }}:</div>
                <div class="flex flex-col items-end">
                  <span>{{ `${shipment.weight} ${t('site_pro.lb')}` }}</span>
                  <span class="text-neutral3 dark:text-dmText3">
                    {{ `${shipment.width} x ${shipment.height} x ${shipment.length} ${t('site_pro.inches')}` }}
                  </span>
                </div>
              </div>
              <div class="flex justify-between text-sm">
                <div>{{ t('site_pro.table.date') }}:</div>
                <div>{{ getFullYear(shipment.created_at) }}</div>
              </div>
              <div class="flex justify-between text-sm">
                <div>{{ t('site_pro.table.shipment_id') }}:</div>
                <div class="font-medium">{{ shipment.id }}</div>
              </div>
            </div>
            <div class="horizontal-divider" />
            <div class="bg-neutral7 dark:bg-dmGray2 rounded p-4">
              <div class="flex gap-4 items-center">
                <div
                  class="collapsible-image"
                  :style="{ backgroundImage: `url('${shipment.media[0]}')`, backgroundSize: 'cover' }"
                  @click="showGalleryModal(shipment)">
                  <div
                    v-if="shipment.media.length > 1"
                    class="flex items-center justify-center bg-black h-full w-full bg-opacity-20 rounded-[0.5rem]">
                    <span class="text-lg font-bold text-white">{{ `+${shipment.media.length}` }}</span>
                  </div>
                </div>
                <AdditionalServicesDropdown :item="shipment" class="w-full" @open-modal="handleOpenServiceModal" />
              </div>
              <div class="bg-white dark:bg-dmGray1 mt-4 px-2 rounded border border-neutral6 dark:border-dmGray4">
                <div
                  v-if="
                    getInspectPackagePrice(shipment, contentStore.additionalServicesConfig?.inspect_package.price) !==
                    null
                  "
                  class="flex justify-between items-center border-b border-neutral6 dark:border-dmGray1 py-5">
                  <div class="text-sm text-text1 dark:text-dmText1 font-normal">
                    {{
                      toPrice(
                        getInspectPackagePrice(shipment, contentStore.additionalServicesConfig?.inspect_package.price)
                      )
                    }}
                  </div>
                  <div class="flex gap-2 text-sm text-text2 dark:text-dmText2">
                    <span class="text-xs">{{ t('site_pro.additional_services.inspect_package.title') }}</span>
                    <Tooltip
                      :text="t('site_pro.additional_services.inspect_package.tooltip_show_modal_description')"
                      trigger="hover">
                      <i
                        class="fa-solid fa-circle-info text-neutral3 dark:text-dmText3 text-base"
                        @click="handleOpenServicesResultsModal(shipment)" />
                    </Tooltip>
                    <Tooltip
                      :text="
                        t('site_pro.additional_services.inspect_package.tooltip_description', {
                          date: getInspectPackageDate(shipment),
                        })
                      ">
                      <i class="fa-solid fa-question-circle text-neutral3 dark:text-dmText3 cursor-pointer text-base" />
                    </Tooltip>
                  </div>
                </div>
                <div
                  v-if="
                    getRequestAdvancedPhotosPrice(
                      shipment,
                      contentStore.additionalServicesConfig?.request_additional_photos.price
                    ) !== null
                  "
                  class="flex justify-between items-center border-b border-neutral6 dark:border-dmGray1 py-5">
                  <div class="text-sm text-text1 dark:text-dmText1 font-normal">
                    {{
                      toPrice(
                        getRequestAdvancedPhotosPrice(
                          shipment,
                          contentStore.additionalServicesConfig?.request_additional_photos.price
                        )
                      )
                    }}
                  </div>
                  <div class="flex gap-2 text-sm text-text2 dark:text-dmText2">
                    <span class="text-xs">{{
                      t('site_pro.additional_services.extra_photos.title', {
                        date: getRequestAdvancedPhotosDate(shipment),
                      })
                    }}</span>
                    <Tooltip
                      :text="
                        t('site_pro.additional_services.extra_photos.tooltip_description', {
                          date: getRequestAdvancedPhotosDate(shipment),
                        })
                      ">
                      <i class="fa-solid fa-question-circle text-neutral3 dark:text-dmText3 cursor-pointer text-base" />
                    </Tooltip>
                  </div>
                </div>
                <div class="flex justify-between items-center py-5">
                  <div class="text-sm text-text1 dark:text-dmText1 font-normal">
                    {{ toPrice(shipment.storage.fee) }}
                  </div>
                  <div class="flex gap-2 text-sm text-text2 dark:text-dmText2">
                    <span class="text-xs">{{ t('site_pro.additional_services.storage_fee.title') }}</span>
                    <Tooltip
                      :text="
                        t('site_pro.additional_services.storage_fee.tooltip_description', {
                          price: toPrice(contentStore.additionalServicesConfig.storage.price_per_day),
                          days_limit: contentStore.additionalServicesConfig.storage.free_days,
                        })
                      ">
                      <i class="fa-solid fa-question-circle text-neutral3 dark:text-dmText3 cursor-pointer text-base" />
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="contentStore.readyItems.length" class="flex justify-center xl:justify-start wl:px-4">
        <Pagination
          :model-value="contentStore.readyItemsCurrentPage"
          :items-total="contentStore.readyItems.length"
          :items-per-page="perPage"
          class="mt-4"
          @update-model="contentStore.setReadyItemsCurrentPage" />
      </div>
    </div>

    <LazyEmptyShipmentsTable v-else top-left-corner :success-message="successMessage" :error-message="errorMessage" />

    <div v-if="contentStore.readyItems.length" class="horizontal-divider xl:border-0 xl:my-2" />
    <div
      v-if="contentStore.readyItems.length"
      class="xl:p-5 xl:bg-white dark:bg-dmGray1 flex flex-col xl:flex-row xl:items-center gap-4 rounded-md">
      <Button
        class="whitespace-nowrap w-full xl:max-w-[16.875rem]"
        :disabled="selectedPackages.length === 0"
        @click="startDelivery">
        {{ `${t('site_pro.start_order')} (${selectedPackages.length})` }}
      </Button>
      <span class="text-sm text-text2 dark:text-dmText2 px-4 xl:px-0">{{
        t('site_pro.next_step_button_description')
      }}</span>
    </div>

    <ModalShipmentItemGallery
      :visible="isModalVisible"
      :item="itemToShowGallery"
      @handle="(value) => handleVisibility(value)" />

    <ModalInspectPackage
      :visible="isModalInspectPackageVisible"
      :item-id="itemIdServiceModal"
      @handle="(value) => (isModalInspectPackageVisible = value)"
      @success="handleSuccessRequestSubmit" />

    <ModalExtraPhotos
      :visible="isModalExtraPhotosVisible"
      :item-id="itemIdServiceModal"
      @handle="(value) => (isModalExtraPhotosVisible = value)"
      @success="handleSuccessRequestSubmit" />

    <ModalAbandonPackage
      :visible="isModalAbandonPackageVisible"
      :item-id="itemIdServiceModal"
      @handle="(value) => (isModalAbandonPackageVisible = value)"
      @success="handleSuccessRequestSubmit" />

    <ModalServicesResults
      :visible="isModalServicesResultsVisible"
      :item="itemToShowServicesResults"
      @handle="(value) => (isModalServicesResultsVisible = value)" />
  </div>
</template>

<style lang="postcss" scoped>
.received-table {
  @apply hidden xl:block text-sm;

  .received-header {
    @apply flex bg-neutral7  items-center py-4 px-7 rounded-t-md;
    @apply dark:bg-dmGray5 dark:border dark:border-dmGray4 dark:border-b-0;

    .header-item-2 {
      @apply w-full max-w-[32.5rem];
    }

    .header-status {
      @apply w-full max-w-[3rem];
    }

    .header-item-3 {
      @apply w-full min-w-[7.5rem] max-w-[11.25rem];
    }

    .header-item-4 {
      @apply w-full max-w-[6.5rem];
    }

    .header-item-5 {
      @apply w-full max-w-[7.5rem];
    }

    .header-item-6 {
      @apply flex justify-end w-16;
    }
  }

  .ready-body {
    .body-item + .body-item {
      @apply border-t;
    }

    :deep(.body-item) {
      @apply border-x border-neutral6 dark:border-dmGray4 dark:bg-dmGray1;

      .body-item-row {
        @apply flex items-center px-7 py-5;

        .body-status {
          @apply w-full max-w-[3rem] flex justify-around;
        }

        .body-item-2 {
          @apply w-full 2xl:max-w-[32.5rem] pr-2;
        }

        .body-item-3 {
          @apply w-full min-w-[5rem] max-w-[11.25rem];
        }

        .body-item-4 {
          @apply w-full max-w-[6.5rem];
        }

        .body-item-5 {
          @apply w-full max-w-[7.5rem];
        }

        .body-item-6 {
          @apply flex justify-end w-16;
        }
      }

      &:last-child {
        @apply rounded-b-md border-b;

        .body-item-collapsible {
          @apply rounded-b-md;
        }
      }

      .body-item-collapsible {
        @apply flex gap-4 px-7 py-2 bg-neutral7 items-end dark:bg-dmGray5;
      }
    }
  }
}

.collapsible-image {
  @apply flex w-20 h-20 min-w-[5rem] min-h-[5rem] rounded-[0.5rem];
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
}

:deep(.input-checkbox-component) {
  .formkit-inner {
    @apply bg-transparent;
  }

  .formkit-wrapper {
    margin-bottom: 0 !important;
  }
}

.ready-table-mobile {
  @apply xl:hidden;

  .mobile-body-item {
    @apply p-4 bg-white dark:bg-dmGray1 border border-neutral7 mb-4 rounded-sm dark:border-dmGray4;

    &.open-item {
      @apply border-brand-primary;
    }
  }

  .mobile-copy-button {
    @apply flex items-center gap-2 w-full rounded-full bg-neutral8 dark:bg-dmGray1 p-4;

    img {
      @apply dark:!invert;

      height: 14px;
      width: 14px;
      filter: invert(41%) sepia(0%) saturate(1%) hue-rotate(235deg) brightness(95%) contrast(90%);
    }

    span {
      @apply text-sm text-black dark:text-white;
    }

    &:hover {
      @apply bg-brand-primaryLight transition cursor-pointer;

      img {
        @apply transition;
        filter: theme('vars.blackToOrangeFilters');
      }

      span {
        @apply text-brand-primary;
      }
    }
  }

  .horizontal-divider {
    @apply border-0 bg-neutral6 my-[1.25rem] dark:bg-dmGray4;
  }
}

.rounded {
  @apply rounded-[0.375rem];
}
</style>

<style lang="postcss" scoped>
.wl {
  .pro-table {
    @apply px-0 lg:bg-neutral7 pb-0 rounded-md rounded-t-none pt-0;

    .ready-body,
    .ready-pagination {
      @apply bg-white;

      :deep(.pagination-items) {
        .pagination-item {
          &.-active {
            @apply !bg-brand-primaryLight;
          }
        }
      }
    }

    .body-item {
      @apply border-x-0 !rounded-none;
    }

    .table-desc {
      @apply lg:mx-7 bg-neutral7 lg:bg-transparent px-4 lg:px-0 lg:border-b border-neutral5;
    }
  }

  .all-toggle {
    @apply bg-neutral7 lg:bg-transparent text-sm;

    span {
      @apply font-normal;
    }
  }

  .toggle {
    @apply bg-brand-primaryLight;
  }
}
</style>
